import { useQuery } from "@tanstack/react-query"
import { LogOut, Menu, Rows2, Rows3, UserRound } from "lucide-react"
import { useNavigate } from "react-router"
import { api } from "@/api/cliente"
import { Campanita } from "@/componentes/avisos/Campanita"
import { esExterno, esTransportista } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { CLAVE_MI_EXPEDIENTE, type MiExpediente } from "@/paginas/interno/MiFicha"
import { Apariencia } from "./Apariencia"

export function Encabezado({
  onAbrirMenu,
  menuAbierto = false,
}: {
  /** Sólo en pantallas estrechas, donde el menú es un cajón (`MenuMovil`). Sin
   *  esto no hay botón: en el escritorio el menú está siempre a la vista. */
  onAbrirMenu?: () => void
  menuAbierto?: boolean
} = {}) {
  const { usuario, densidad, setDensidad, setUsuario } = usarSesion()
  const navegar = useNavigate()
  // (23/10) «Mi ficha»: el expediente laboral propio, con cuántos documentos le faltan.
  // El icono sale SIEMPRE al personal (23/10): sin ficha enlazada el servidor responde
  // 404 y la página lo dice. Escondido, quien no la tiene no sabía que existía.
  const miFicha = useQuery({
    queryKey: CLAVE_MI_EXPEDIENTE,
    queryFn: () => api.get<MiExpediente>("/api/v1/mi-expediente"),
    enabled: usuario !== null && !esExterno(usuario),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const faltan = miFicha.data?.pendientes.length ?? 0

  async function salir() {
    try {
      await api.post("/api/v1/auth/logout")
    } finally {
      // Se limpia la sesión local pase lo que pase: si el servidor ya la había
      // invalidado, insistir aquí sólo dejaría al usuario atrapado en una
      // pantalla que va a dar 401 en cada request.
      setUsuario(null)
      navegar("/login", { replace: true })
    }
  }

  return (
    <header
      // Sin `border-b`: desde que el encabezado es un panel flotante, ese borde es
      // una línea a lo ancho justo donde el panel se redondea, y las esquinas la
      // cortan. Lo que separa ahora del área de trabajo es el hueco entre paneles.
      // El MISMO cristal oscuro que el menú. Con el menú en oscuro y esta franja en
      // claro, el marco se parte en dos materiales distintos y deja de leerse como
      // una sola cosa flotando sobre la fotografía.
      // ⚠ **`relative z-30`: sin ella los desplegables quedan DEBAJO del área de
      // trabajo.** El `backdrop-filter` del cristal encierra al encabezado en su propia
      // capa, y el área —también de cristal, y después en el documento— se pinta encima:
      // el `z-50` de la campanita sólo cuenta dentro del encabezado (09/10).
      className="marco-cristal relative z-30 flex h-14 shrink-0 items-center justify-between gap-1 px-2 sm:px-4"
    >
      {onAbrirMenu !== undefined && (
        <button
          type="button"
          onClick={onAbrirMenu}
          aria-label="Abrir el menú"
          aria-expanded={menuAbierto}
          aria-haspopup="dialog"
          // 44 px: es lo que más se pulsa en el teléfono, y con el pulgar.
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-control)] text-[rgb(var(--marco-tinta)/0.9)] toque-cristal foco-cristal"
        >
          <Menu size={22} strokeWidth={1.5} aria-hidden="true" />
        </button>
      )}

      {/* El nombre lleva a la propia cuenta: es donde se busca, y evita una entrada
          mas en la barra lateral para algo que se toca dos veces al ano. */}
      <button
        type="button"
        // El intermediario de transporte no tiene «Mi cuenta»: su portal sólo alcanza
        // `/api/v1/transporte/` (25/10). El nombre se queda, sin llevar a ningún sitio.
        onClick={() => !esTransportista(usuario) && navegar("/perfil")}
        // `min-w-0` y `truncate`: en un teléfono de 320 px el nombre completo
        // empujaría los botones de la derecha fuera de la pantalla.
        className="mr-auto min-w-0 truncate rounded-[var(--radius-control)] px-2 py-1 text-sm toque-cristal foco-cristal"
        // Blanco tenue: `--fg-muted` es un gris calibrado contra la superficie del
        // tema y sobre este cristal se hunde.
        style={{ color: "rgb(var(--marco-tinta) / 0.75)" }}
      >
        {usuario?.nombre}
      </button>

      <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
        {/* Sólo para el personal: los avisos son de trabajo interno y un importador
            no tiene ninguno. Enseñarle una campanita que nunca se enciende es
            prometerle algo que no existe. */}
        {!esExterno(usuario) && <Campanita />}

        {!esExterno(usuario) && (
          <button
            type="button"
            onClick={() => navegar("/mi-ficha")}
            aria-label={
              faltan > 0 ? `Mi ficha laboral: te faltan ${faltan} documentos` : "Mi ficha laboral"
            }
            title="Mi ficha laboral"
            className="relative flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] text-[rgb(var(--marco-tinta)/0.85)] toque-cristal foco-cristal"
          >
            <UserRound size={20} strokeWidth={1.5} aria-hidden="true" />
            {faltan > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 grid min-w-4 place-items-center rounded-full px-1 text-[10px] font-semibold tabular-nums"
                style={{ backgroundColor: "var(--warning)", color: "var(--primary-fg)" }}
              >
                {faltan}
              </span>
            )}
          </button>
        )}

        <Apariencia />

        <button
          type="button"
          onClick={() => setDensidad(densidad === "compacta" ? "comoda" : "compacta")}
          aria-label={
            densidad === "compacta" ? "Cambiar a densidad cómoda" : "Cambiar a densidad compacta"
          }
          // La densidad es cosa de tablas largas en un monitor; en el teléfono sólo
          // ocuparía el sitio que le falta al nombre.
          className="hidden h-9 w-9 items-center justify-center rounded-[var(--radius-control)] text-[rgb(var(--marco-tinta)/0.85)] toque-cristal foco-cristal sm:flex"
        >
          {densidad === "compacta" ? (
            <Rows3 size={20} strokeWidth={1.5} aria-hidden="true" />
          ) : (
            <Rows2 size={20} strokeWidth={1.5} aria-hidden="true" />
          )}
        </button>

        <button
          type="button"
          onClick={salir}
          aria-label="Cerrar sesión"
          className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] text-[rgb(var(--marco-tinta)/0.85)] toque-cristal foco-cristal"
        >
          <LogOut size={20} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>
    </header>
  )
}
